import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams, clampPayloadToCurrentScope, filterItemsByCurrentScope } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractLocationFields } from '../utils/scopeFields';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid, itemKeys = []) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid || item?.id === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (candidate.uuid || candidate.id || itemKeys.some((key) => candidate?.[key] != null)) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({
  basePath,
  normalize,
  buildPayload,
  defaultPerPage = 25,
  itemKeys = [],
  collectionKey,
}) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    const page = extractCollection(data, normalize);
    return {
      ...page,
      items: filterItemsByCurrentScope(page.items),
    };
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid, itemKeys);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize, null, itemKeys);
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return data;
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'submit', uuid });
      return item;
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'validate', uuid });
      return item;
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'approve', uuid });
      return item;
    },

    async reject(uuid, notes = '') {
      if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes: notes.trim() });
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'reject', uuid });
      return item;
    },
  };
}

const ACTOR_TYPE_MAP = {
  veterinarian: 'Veterinarian',
  animal_scientist: 'Animal Scientist',
  extension_worker: 'Extension Worker',
  livestock_inspector: 'Livestock Inspector',
  abattoir_officer: 'Abattoir Officer',
  vet_paraprofessional: 'Vet Paraprofessional',
  cahw: 'CAHW',
  cooperative_officer: 'Cooperative Officer',
  trader: 'Trader',
  farmer_producer: 'Farmer Producer',
  processor: 'Processor',
  transporter: 'Transporter',
  input_supplier: 'Input Supplier',
  researcher: 'Researcher',
  other: 'Other',
};

const ACTOR_TYPE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(ACTOR_TYPE_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const SEX_MAP = {
  male: 'Male',
  female: 'Female',
  prefer_not_to_say: 'Prefer Not To Say',
};
const SEX_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(SEX_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const AGE_GROUP_MAP = {
  '18_to_25': '18 to 25',
  '26_to_35': '26 to 35',
  '36_to_45': '36 to 45',
  '46_to_55': '46 to 55',
  '56_plus': '56+',
};
const AGE_GROUP_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(AGE_GROUP_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const STATUS_MAP = {
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
  on_leave: 'On Leave',
  completed: 'Completed',
};
const STATUS_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(STATUS_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const VERIFY_MAP = {
  verified: 'Verified',
  pending: 'Pending',
  rejected: 'Rejected',
};
const VERIFY_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(VERIFY_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const QUAL_TYPE_MAP = {
  degree_vet_medicine: 'Degree Vet Medicine',
  degree_animal_science: 'Degree Animal Science',
  diploma: 'Diploma',
  certificate: 'Certificate',
  professional_licence: 'Professional License',
  short_course: 'Short Course',
  extension_training: 'Extension Training',
  cpd: 'CPD',
  other: 'Other',
};
const QUAL_TYPE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(QUAL_TYPE_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const PRODUCTION_SYSTEM_MAP = {
  pastoral: 'Pastoral',
  agropastoral: 'Agropastoral',
  intensive: 'Intensive',
  semi_intensive: 'Semi Intensive',
  backyard: 'Backyard',
  mixed: 'Mixed',
};
const PRODUCTION_SYSTEM_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(PRODUCTION_SYSTEM_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const DEPLOYMENT_TYPE_MAP = {
  permanent: 'Permanent',
  temporary: 'Temporary',
  field_mission: 'Field Mission',
  training_attachment: 'Training Attachment',
  emergency: 'Emergency',
};
const DEPLOYMENT_TYPE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(DEPLOYMENT_TYPE_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const VEHICLE_STATUS_MAP = {
  has_vehicle: 'Has Vehicle',
  shared_vehicle: 'Shared Vehicle',
  no_vehicle: 'No Vehicle',
};
const VEHICLE_STATUS_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(VEHICLE_STATUS_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const COOP_TYPE_MAP = {
  farmers_cooperative: 'Farmers Cooperative',
  commodity_association: 'Commodity Association',
  processors_association: 'Processors Association',
  traders_association: 'Traders Association',
  womens_group: 'Womens Group',
  youth_group: 'Youth Group',
  ngo: 'NGO',
  private_company: 'Private Company',
  government_agency: 'Government Agency',
  other: 'Other',
};
const COOP_TYPE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(COOP_TYPE_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const REG_AUTH_MAP = {
  cac: 'CAC',
  fmld: 'FMLD',
  state_ministry: 'State Ministry',
  cooperative_society: 'Cooperative Society',
  other: 'Other',
};
const REG_AUTH_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(REG_AUTH_MAP).map(([code, label]) => [normalizeKey(label), code])
);

const BANK_STATUS_MAP = {
  yes: 'Active',
  no: 'Inactive',
  pending: 'Pending',
};
const BANK_STATUS_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(BANK_STATUS_MAP).map(([code, label]) => [normalizeKey(label), code])
);

function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function toDisplayLabel(value, map) {
  if (value === null || value === undefined || value === '') return '';
  const key = normalizeKey(value);
  return map[key] ?? String(value);
}

function toApiValue(value, labelToCodeMap, fallbackMap = null) {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = normalizeKey(raw).replace(/_/g, '_');
  if (labelToCodeMap[normalized]) return labelToCodeMap[normalized];
  if (fallbackMap && fallbackMap[normalized]) return normalized;
  if (raw.includes('_') && labelToCodeMap[raw.toLowerCase()]) return labelToCodeMap[raw.toLowerCase()];
  return raw.toLowerCase();
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function formatDateValue(value) {
  if (!value) return '';
  const text = String(value).trim();
  if (!text) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  const dateParts = text.split(/[-/]/);
  if (dateParts.length === 3 && dateParts[2].length === 4) {
    const [first, second, year] = dateParts;
    const firstNum = Number(first);
    const secondNum = Number(second);

    if (firstNum > 12 && secondNum <= 12) {
      return `${year}-${second}-${first}`;
    }

    return `${year}-${first}-${second}`;
  }

  return text;
}

function displayDateValue(value) {
  if (!value) return '';
  const text = String(value).trim();
  if (!text) return '';

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${m}/${d}/${y}`;
  }

  return text;
}

function resolveName(row = {}, keys = []) {
  for (const key of keys) {
    const value = row?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function resolveNestedValue(row = {}, paths = []) {
  for (const path of paths) {
    let current = row;
    let valid = true;
    for (const segment of path.split('.')) {
      if (current && typeof current === 'object' && segment in current) {
        current = current[segment];
      } else {
        valid = false;
        break;
      }
    }
    if (valid && current !== null && current !== undefined && String(current).trim()) {
      return current;
    }
  }
  return '';
}

function normalizeLivestockActor(row = {}) {
  const location = extractLocationFields(row);
  const actorType = row.actor_type ?? row.actorType ?? '';
  const sex = row.sex ?? '';
  const ageGroup = row.age_group ?? row.ageGroup ?? '';
  const status = row.status ?? '';
  const verificationStatus = row.verification_status ?? row.verificationStatus ?? '';
  const contactPhone = row.contact_phone ?? row.contactPhone ?? '';
  const registrationNumber = row.registration_number ?? row.registrationNumber ?? '';
  const speciesSpecialisation = row.species_specialisation ?? row.speciesSpecialisation ?? '';
  const geospatialId = row.geospatial_id ?? row.geospatialId ?? '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    full_name: row.full_name ?? row.fullName ?? '',
    fullName: row.full_name ?? row.fullName ?? '',
    contact_phone: contactPhone,
    contactPhone,
    phone: contactPhone,
    actor_type: toDisplayLabel(actorType, ACTOR_TYPE_MAP),
    actorType: toDisplayLabel(actorType, ACTOR_TYPE_MAP),
    actor_type_code: actorType,
    gender: toDisplayLabel(sex, SEX_MAP),
    sex: toDisplayLabel(sex, SEX_MAP),
    sex_code: sex,
    age_group: toDisplayLabel(ageGroup, AGE_GROUP_MAP),
    ageGroup: toDisplayLabel(ageGroup, AGE_GROUP_MAP),
    age_group_code: ageGroup,
    registration_number: registrationNumber,
    registrationNumber,
    species_specialisation: speciesSpecialisation,
    speciesSpecialisation,
    geospatial_id: geospatialId,
    geospatialId,
    status: toDisplayLabel(status, STATUS_MAP),
    status_code: status,
    // Record workflow status (draft → submitted → validated → approved),
    // separate from the actor's Active/Inactive `status`.
    data_status: String(row.data_status ?? row.dataStatus ?? 'draft').toLowerCase(),
    rejection_notes: row.rejection_notes ?? row.rejectionNotes ?? null,
    verification_status: toDisplayLabel(verificationStatus, VERIFY_MAP),
    verificationStatus: toDisplayLabel(verificationStatus, VERIFY_MAP),
    verification_status_code: verificationStatus,
    state: location.state ?? '',
    lga: location.lga ?? '',
    ward_community: row.ward_community ?? row.wardCommunity ?? '',
    wardCommunity: row.ward_community ?? row.wardCommunity ?? '',
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildLivestockActorPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return {
    full_name: trimOrNull(payload.full_name ?? payload.fullName),
    actor_type: toApiValue(payload.actor_type ?? payload.actorType, ACTOR_TYPE_LABEL_TO_CODE),
    actor_type_code: toApiValue(payload.actor_type ?? payload.actorType, ACTOR_TYPE_LABEL_TO_CODE),
    gender: toApiValue(payload.gender ?? payload.sex, SEX_LABEL_TO_CODE),
    sex: toApiValue(payload.sex ?? payload.gender, SEX_LABEL_TO_CODE),
    age_group: toApiValue(payload.age_group ?? payload.ageGroup, AGE_GROUP_LABEL_TO_CODE),
    contact_phone: trimOrNull(payload.contact_phone ?? payload.contactPhone ?? payload.phone),
    phone: trimOrNull(payload.phone ?? payload.contact_phone ?? payload.contactPhone),
    state: trimOrNull(scopedPayload.state),
    lga: trimOrNull(scopedPayload.lga),
    ward_community: trimOrNull(payload.ward_community ?? payload.wardCommunity),
    geospatial_id: trimOrNull(payload.geospatial_id ?? payload.geospatialId),
    registration_number: trimOrNull(payload.registration_number ?? payload.registrationNumber),
    species_specialisation: trimOrNull(payload.species_specialisation ?? payload.speciesSpecialisation),
    status: toApiValue(payload.status, STATUS_LABEL_TO_CODE),
    verification_status: toApiValue(payload.verification_status ?? payload.verificationStatus, VERIFY_LABEL_TO_CODE),
  };
}

function normalizeActorQualification(row = {}) {
  const location = extractLocationFields(row);
  const qualificationType = row.qualification_type ?? row.qualificationType ?? '';
  const verificationStatus = row.verification_status ?? row.verificationStatus ?? '';
  const actorName = resolveName(row, ['full_name', 'fullName', 'actor_name', 'actorName']) ||
    resolveNestedValue(row, ['livestock_actor.full_name', 'livestock_actor.name', 'actor.full_name', 'actor.name']);
  const institutionName = resolveName(row, ['institution_name', 'institutionName', 'awarded_institution', 'awardedInstitution']) ||
    resolveNestedValue(row, ['issuing_institution.name', 'issuingInstitution.name']);

  const issueDate = row.issue_date ?? row.issueDate ?? '';
  const expiryDate = row.expiry_date ?? row.expiryDate ?? row.expiry_renewal_date ?? row.expiryRenewalDate ?? '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    fullName: actorName,
    livestock_actor_uuid: row.livestock_actor_uuid ?? row.livestockActorUuid ?? row.actor_uuid ?? '',
    qualificationType: toDisplayLabel(qualificationType, QUAL_TYPE_MAP),
    qualification_type: qualificationType,
    qualificationName: row.qualification_name ?? row.qualificationName ?? row.specialization ?? '',
    qualification_name: row.qualification_name ?? row.qualificationName ?? row.specialization ?? '',
    issuing_institution_uuid: row.issuing_institution_uuid ?? row.issuingInstitutionUuid ?? '',
    issuingInstitutionUuid: row.issuing_institution_uuid ?? row.issuingInstitutionUuid ?? '',
    institution: institutionName,
    issuing_authority: row.issuing_authority ?? row.issuingAuthority ?? '',
    issuingAuthority: row.issuing_authority ?? row.issuingAuthority ?? '',
    issue_date: issueDate,
    issueDate,
    expiry_date: expiryDate,
    expiryDate,
    yearAwarded: issueDate ? String(issueDate).slice(0, 4) : String(row.year_awarded ?? row.yearAwarded ?? ''),
    specialization: row.qualification_name ?? row.qualificationName ?? row.specialization ?? '',
    credential_number: row.credential_number ?? row.credentialNumber ?? '',
    credentialNumber: row.credential_number ?? row.credentialNumber ?? '',
    evidence_attachment: row.evidence_attachment ?? row.evidenceAttachment ?? '',
    evidenceAttachment: row.evidence_attachment ?? row.evidenceAttachment ?? '',
    evidence_attachment_file_name: row.evidence_attachment_file_name ?? row.evidenceAttachmentFileName ?? '',
    evidenceAttachmentFileName: row.evidence_attachment_file_name ?? row.evidenceAttachmentFileName ?? '',
    verification_status: toDisplayLabel(verificationStatus, VERIFY_MAP),
    verificationStatus: toDisplayLabel(verificationStatus, VERIFY_MAP),
    verification_status_code: verificationStatus,
    state: location.state ?? '',
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildActorQualificationPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return {
    livestock_actor_uuid: trimOrNull(payload.livestock_actor_uuid ?? payload.livestockActorUuid),
    qualification_type: toApiValue(payload.qualification_type ?? payload.qualificationType, QUAL_TYPE_LABEL_TO_CODE),
    qualification_name: trimOrNull(payload.qualification_name ?? payload.qualificationName ?? payload.specialization ?? payload.fullName),
    issuing_institution_uuid: trimOrNull(payload.issuing_institution_uuid ?? payload.issuingInstitutionUuid),
    issuing_authority: trimOrNull(payload.issuing_authority ?? payload.issuingAuthority),
    issue_date: formatDateValue(payload.issue_date ?? payload.issueDate ?? payload.yearAwarded),
    expiry_date: formatDateValue(payload.expiry_date ?? payload.expiryDate ?? payload.expiryRenewal),
    credential_number: trimOrNull(payload.credential_number ?? payload.credentialNumber),
    evidence_attachment: trimOrNull(payload.evidence_attachment ?? payload.evidenceAttachment),
    evidence_attachment_file_name: trimOrNull(payload.evidence_attachment_file_name ?? payload.evidenceAttachmentFileName),
    verification_status: toApiValue(payload.verification_status ?? payload.verificationStatus, VERIFY_LABEL_TO_CODE),
    state: trimOrNull(scopedPayload.state),
  };
}

function normalizeWorkforceDeployment(row = {}) {
  const location = extractLocationFields(row);
  const roleCategory = row.role_category ?? row.roleCategory ?? row.actor_type ?? row.actorType ?? '';
  const deploymentType = row.deployment_type ?? row.deploymentType ?? '';
  const deploymentStatus = row.deployment_status ?? row.deploymentStatus ?? row.status ?? '';
  const vehicleStatus = row.vehicle_status ?? row.vehicleStatus ?? '';
  const geospatialId = row.geospatial_id ?? row.geospatialId ?? '';

  const actorName = resolveName(row, ['actor_name', 'actorName', 'full_name', 'fullName']) ||
    resolveNestedValue(row, ['livestock_actor.full_name', 'livestock_actor.name', 'actor.full_name', 'actor.name']);
  const institutionName = resolveName(row, ['posting_institution', 'postingInstitution', 'institution_name', 'institutionName']) ||
    resolveNestedValue(row, ['institution.name', 'posting_institution.name']);
  const supervisor = resolveName(row, ['supervisor', 'supervisor_name', 'supervisorName']);

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    actorName,
    livestock_actor_uuid: row.livestock_actor_uuid ?? row.livestockActorUuid ?? '',
    actorId: row.livestock_actor_uuid ?? row.livestockActorUuid ?? '',
    actorType: toDisplayLabel(roleCategory, ACTOR_TYPE_MAP),
    role_category: roleCategory,
    roleCategory: toDisplayLabel(roleCategory, ACTOR_TYPE_MAP),
    postingInstitution: institutionName,
    institution_uuid: row.institution_uuid ?? row.institutionUuid ?? '',
    institutionFacility: institutionName,
    institution_facility: row.institution_uuid ?? row.institutionUuid ?? '',
    geospatial_id: geospatialId,
    geospatialId,
    status: toDisplayLabel(deploymentStatus, STATUS_MAP),
    deployment_status: deploymentStatus,
    // Record workflow status, separate from the deployment `status` above.
    data_status: String(row.data_status ?? row.dataStatus ?? 'draft').toLowerCase(),
    rejection_notes: row.rejection_notes ?? row.rejectionNotes ?? null,
    deploymentType: toDisplayLabel(deploymentType, DEPLOYMENT_TYPE_MAP),
    deployment_type: deploymentType,
    mobility_status: vehicleStatus,
    mobilityStatus: toDisplayLabel(vehicleStatus, VEHICLE_STATUS_MAP),
    vehicle_status: vehicleStatus,
    state: location.state ?? '',
    lga: location.lga ?? '',
    ward_community: row.ward_community ?? row.wardCommunity ?? '',
    wardCommunity: row.ward_community ?? row.wardCommunity ?? '',
    startDate: displayDateValue(row.start_date ?? row.startDate ?? ''),
    start_date: row.start_date ?? row.startDate ?? '',
    endDate: displayDateValue(row.end_date ?? row.endDate ?? ''),
    end_date: row.end_date ?? row.endDate ?? '',
    supervisor,
    responsibilities: row.responsibilities ?? '',
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildWorkforceDeploymentPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return {
    livestock_actor_uuid: trimOrNull(payload.livestock_actor_uuid ?? payload.livestockActorUuid ?? payload.actor_id ?? payload.actorId),
    actor_id: trimOrNull(payload.actor_id ?? payload.actorId ?? payload.livestock_actor_uuid ?? payload.livestockActorUuid),
    institution_uuid: trimOrNull(payload.institution_uuid ?? payload.institutionUuid ?? payload.institution_facility ?? payload.institutionFacility),
    institution_facility: trimOrNull(payload.institution_facility ?? payload.institutionFacility ?? payload.institution_uuid ?? payload.institutionUuid),
    role_category: toApiValue(payload.role_category ?? payload.roleCategory ?? payload.actorType, ACTOR_TYPE_LABEL_TO_CODE),
    deployment_type: toApiValue(payload.deployment_type ?? payload.deploymentType, DEPLOYMENT_TYPE_LABEL_TO_CODE),
    deployment_status: toApiValue(payload.deployment_status ?? payload.deploymentStatus ?? payload.status, STATUS_LABEL_TO_CODE),
    vehicle_status: toApiValue(payload.vehicle_status ?? payload.vehicleStatus ?? payload.mobilityStatus ?? payload.mobility_status, VEHICLE_STATUS_LABEL_TO_CODE),
    mobility_status: toApiValue(payload.mobility_status ?? payload.mobilityStatus ?? payload.vehicleStatus, VEHICLE_STATUS_LABEL_TO_CODE),
    state: trimOrNull(scopedPayload.state),
    lga: trimOrNull(scopedPayload.lga),
    ward_community: trimOrNull(payload.ward_community ?? payload.wardCommunity),
    geospatial_id: trimOrNull(payload.geospatial_id ?? payload.geospatialId),
    start_date: formatDateValue(payload.start_date ?? payload.startDate),
    end_date: formatDateValue(payload.end_date ?? payload.endDate),
    supervisor: trimOrNull(payload.supervisor),
    responsibilities: trimOrNull(payload.responsibilities),
  };
}

function normalizeFarmerProducer(row = {}) {
  const location = extractLocationFields(row);
  const productionSystem = row.production_system ?? row.productionSystem ?? '';
  const herdSizeClass = row.herd_size_class ?? row.herdSizeClass ?? '';
  const status = row.status ?? '';
  const dataStatus = String(row.data_status ?? row.dataStatus ?? 'draft').toLowerCase() || 'draft';
  const farmerName = resolveName(row, ['farmer_name', 'farmerName', 'full_name', 'fullName']) ||
    resolveNestedValue(row, ['livestock_actor.full_name', 'livestock_actor.name', 'actor.full_name', 'actor.name']);
  const speciesName = resolveName(row, ['species_name', 'speciesName']) ||
    resolveNestedValue(row, ['species.common_name_nigeria', 'species.name', 'species.label', 'species.commonNameNigeria']);
  const marketName = resolveName(row, ['primary_market_name', 'primaryMarketName']) ||
    resolveNestedValue(row, ['primary_market.market_name', 'primary_market.name', 'primaryMarket.market_name']);
  const extensionOfficerName = resolveName(row, ['extension_officer_name', 'extensionOfficerName']) ||
    resolveNestedValue(row, ['extension_officer.full_name', 'extension_officer.name', 'extensionOfficer.full_name']);
  const populationRecordUuid =
    resolveNestedValue(row, ['population_record.uuid', 'populationRecord.uuid']) ||
    row.population_record_uuid ||
    row.populationRecordUuid ||
    '';
  const populationRecordRef =
    resolveNestedValue(row, ['population_record.reference_period', 'populationRecord.reference_period', 'population_record.title', 'populationRecord.title']) ||
    resolveNestedValue(row, ['population_record.period', 'populationRecord.period']) ||
    '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    farmerName,
    livestock_actor_uuid: row.livestock_actor_uuid ?? row.livestockActorUuid ?? '',
    productionSystem: toDisplayLabel(productionSystem, PRODUCTION_SYSTEM_MAP),
    production_system: productionSystem,
    species: speciesName,
    species_uuid: row.species_uuid ?? row.speciesUuid ?? resolveNestedValue(row, ['species.uuid']) ?? '',
    herdSizeClass: toDisplayLabel(herdSizeClass, { large: 'Large', medium: 'Medium', small: 'Small', micro: 'Micro' }),
    herd_size_class: herdSizeClass,
    farmSize: row.farm_size ?? row.farmSize ?? '',
    farm_size: row.farm_size ?? row.farmSize ?? '',
    state: location.state ?? '',
    farmHerdRegNo: row.herd_registration_number ?? row.herdRegistrationNumber ?? '',
    herd_registration_number: row.herd_registration_number ?? row.herdRegistrationNumber ?? '',
    primaryMarketRef: marketName,
    primary_market_uuid: row.primary_market_uuid ?? row.primaryMarketUuid ?? resolveNestedValue(row, ['primary_market.uuid']) ?? '',
    extensionOfficer: extensionOfficerName || (row.extension_officer_deployment_uuid ? 'Assigned' : ''),
    extension_officer_deployment_uuid: row.extension_officer_deployment_uuid ?? row.extensionOfficerDeploymentUuid ?? resolveNestedValue(row, ['extension_officer_deployment.uuid']) ?? '',
    populationRecord: populationRecordRef,
    populationRecordUuid: populationRecordUuid,
    population_record_uuid: populationRecordUuid,
    lga: location.lga ?? '',
    dataStatus,
    data_status: dataStatus,
    status: toDisplayLabel(status, STATUS_MAP),
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildFarmerProducerPayload(payload = {}) {
  const herdRegistration = trimOrNull(payload.herd_registration_number ?? payload.herdRegistrationNumber ?? payload.farmHerdRegNo);
  const extensionOfficerUuid = trimOrNull(payload.extension_officer_deployment_uuid ?? payload.extensionOfficerDeploymentUuid);
  return {
    livestock_actor_uuid: trimOrNull(payload.livestock_actor_uuid ?? payload.livestockActorUuid),
    species_uuid: trimOrNull(payload.species_uuid ?? payload.speciesUuid),
    production_system: toApiValue(payload.production_system ?? payload.productionSystem, PRODUCTION_SYSTEM_LABEL_TO_CODE),
    herd_size_class: toApiValue(payload.herd_size_class ?? payload.herdSizeClass, {
      large: 'large',
      medium: 'medium',
      small: 'small',
      micro: 'micro',
    }),
    farm_size: trimOrNull(payload.farm_size ?? payload.farmSize),
    herd_registration_number: herdRegistration,
    population_record_uuid: trimOrNull(payload.population_record_uuid ?? payload.populationRecordUuid),
    primary_market_uuid: trimOrNull(payload.primary_market_uuid ?? payload.primaryMarketUuid),
    extension_officer_deployment_uuid: extensionOfficerUuid,
  };
}

function normalizeCooperative(row = {}) {
  const location = extractLocationFields(row);
  const cooperativeType = row.cooperative_type ?? row.cooperativeType ?? '';
  const registeringAuthority = row.registering_authority ?? row.registeringAuthority ?? '';
  const bankAccountStatus = row.bank_account_status ?? row.bankAccountStatus ?? '';
  const status = row.status ?? '';
  const dataStatus = String(row.data_status ?? row.dataStatus ?? 'draft').toLowerCase() || 'draft';
  const orgName = resolveName(row, ['cooperative_name', 'organisation_name', 'organization_name', 'orgName', 'name']);
  const chairperson = resolveName(row, ['chairperson_name', 'chairpersonName']) ||
    resolveNestedValue(row, ['chairperson.full_name', 'chairperson.name', 'chairperson.label', 'chairperson.fullName']);
  const geospatialId = row.geospatial_id ?? row.geospatialId ?? '';
  const primaryCommodity = row.primary_commodity ?? row.primaryCommodity ?? row.primary_activities ?? row.primaryActivities ?? '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    orgName,
    cooperative_name: orgName,
    organisation_name: orgName,
    cooperative_type: cooperativeType,
    cooperativeType: toDisplayLabel(cooperativeType, COOP_TYPE_MAP),
    organisation_type: cooperativeType,
    organisationType: toDisplayLabel(cooperativeType, COOP_TYPE_MAP),
    regNumber: row.registration_number ?? row.registrationNumber ?? row.regNumber ?? '',
    registration_number: row.registration_number ?? row.registrationNumber ?? row.regNumber ?? '',
    reg_number_org: row.registration_number ?? row.registrationNumber ?? row.regNumber ?? '',
    state: location.state ?? '',
    lga: location.lga ?? '',
    geospatial_id: geospatialId,
    geospatialId,
    ward_community: row.ward_community ?? row.wardCommunity ?? '',
    wardCommunity: row.ward_community ?? row.wardCommunity ?? '',
    totalMembers: toNumberOrNull(row.total_members ?? row.totalMembers),
    total_members: toNumberOrNull(row.total_members ?? row.totalMembers),
    activeMembers: toNumberOrNull(row.active_members ?? row.activeMembers),
    active_members: toNumberOrNull(row.active_members ?? row.activeMembers),
    registeringAuthority: toDisplayLabel(registeringAuthority, REG_AUTH_MAP),
    registering_authority: registeringAuthority,
    dateOfRegistration: displayDateValue(row.registration_date ?? row.dateOfRegistration ?? row.created_at ?? ''),
    registration_date: row.registration_date ?? row.dateOfRegistration ?? row.created_at ?? '',
    date_of_registration: row.date_of_registration ?? row.dateOfRegistration ?? row.registration_date ?? row.created_at ?? '',
    bankAccountStatus: toDisplayLabel(bankAccountStatus, BANK_STATUS_MAP),
    bank_account_status: bankAccountStatus,
    primary_commodity: primaryCommodity,
    primaryCommodity,
    primaryActivities: Array.isArray(row.primary_activities ?? row.primaryActivities)
      ? (row.primary_activities ?? row.primaryActivities).join(', ')
      : (row.primary_activities ?? row.primaryActivities ?? ''),
    primary_activities: row.primary_activities ?? row.primaryActivities ?? '',
    contact_phone: row.contact_phone ?? row.contactPhone ?? '',
    contactPhone: row.contact_phone ?? row.contactPhone ?? '',
    contact_email: row.contact_email ?? row.contactEmail ?? '',
    contactEmail: row.contact_email ?? row.contactEmail ?? '',
    chairperson: chairperson ? { name: chairperson } : (row.chairperson ?? null),
    chairperson_actor_uuid: row.chairperson_actor_uuid ?? row.chairpersonActorUuid ?? '',
    status: toDisplayLabel(status, STATUS_MAP),
    status_code: status,
    dataStatus,
    data_status: dataStatus,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildCooperativePayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return {
    cooperative_name: trimOrNull(payload.cooperative_name ?? payload.orgName ?? payload.organisation_name),
    organisation_name: trimOrNull(payload.organisation_name ?? payload.orgName ?? payload.cooperative_name),
    cooperative_type: toApiValue(payload.cooperative_type ?? payload.cooperativeType ?? payload.organisation_type ?? payload.organisationType, COOP_TYPE_LABEL_TO_CODE),
    organisation_type: toApiValue(payload.organisation_type ?? payload.organisationType ?? payload.cooperativeType ?? payload.cooperative_type, COOP_TYPE_LABEL_TO_CODE),
    registration_number: trimOrNull(payload.registration_number ?? payload.regNumber ?? payload.reg_number_org),
    reg_number_org: trimOrNull(payload.reg_number_org ?? payload.regNumber ?? payload.registration_number),
    state: trimOrNull(scopedPayload.state),
    lga: trimOrNull(scopedPayload.lga ?? scopedPayload.lgaName),
    ward_community: trimOrNull(scopedPayload.ward_community ?? scopedPayload.wardCommunity),
    total_members: toNumberOrNull(payload.total_members ?? payload.totalMembers),
    active_members: toNumberOrNull(payload.active_members ?? payload.activeMembers),
    registering_authority: toApiValue(payload.registering_authority ?? payload.registeringAuthority, REG_AUTH_LABEL_TO_CODE),
    registration_date: formatDateValue(payload.registration_date ?? payload.dateOfRegistration),
    date_of_registration: formatDateValue(payload.date_of_registration ?? payload.dateOfRegistration ?? payload.registration_date),
    bank_account_status: toApiValue(payload.bank_account_status ?? payload.bankAccountStatus, BANK_STATUS_LABEL_TO_CODE),
    geospatial_id: trimOrNull(payload.geospatial_id ?? payload.geospatialId),
    primary_activities: Array.isArray(payload.primary_activities ?? payload.primaryActivities)
      ? (payload.primary_activities ?? payload.primaryActivities).map((item) => String(item).trim()).filter(Boolean)
      : trimOrNull(payload.primary_activities ?? payload.primaryActivities ?? payload.primary_commodity ?? payload.primaryCommodity),
    primary_commodity: trimOrNull(payload.primary_commodity ?? payload.primaryCommodity),
    contact_phone: trimOrNull(payload.contact_phone ?? payload.contactPhone),
    contact_email: trimOrNull(payload.contact_email ?? payload.contactEmail),
    chairperson_actor_uuid: trimOrNull(payload.chairperson_actor_uuid ?? payload.chairpersonActorUuid),
    status: toApiValue(payload.status, STATUS_LABEL_TO_CODE),
  };
}

export const livestockActorsApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/livestock-actors',
  normalize: normalizeLivestockActor,
  buildPayload: buildLivestockActorPayload,
  defaultPerPage: 200,
  itemKeys: ['full_name', 'fullName', 'actor_type', 'actorType', 'state', 'lga'],
  collectionKey: 'livestock-actors',
}), {
  basePath: '/v1/livestock-actors',
  collectionKey: 'livestock-actors',
  normalize: normalizeLivestockActor,
  buildPayload: buildLivestockActorPayload,
});

export const actorQualificationsApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/actor-qualifications',
  normalize: normalizeActorQualification,
  buildPayload: buildActorQualificationPayload,
  defaultPerPage: 200,
  itemKeys: ['qualification_type', 'qualificationType', 'qualification_name', 'qualificationName', 'issue_date', 'issueDate', 'expiry_date', 'expiryDate', 'credential_number'],
  collectionKey: 'actor-qualifications',
}), {
  basePath: '/v1/actor-qualifications',
  collectionKey: 'actor-qualifications',
  normalize: normalizeActorQualification,
  buildPayload: buildActorQualificationPayload,
});

export const workforceDeploymentsApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/workforce-deployments',
  normalize: normalizeWorkforceDeployment,
  buildPayload: buildWorkforceDeploymentPayload,
  defaultPerPage: 200,
  itemKeys: ['role_category', 'roleCategory', 'deployment_type', 'deploymentType', 'deployment_status', 'deploymentStatus', 'vehicle_status', 'vehicleStatus'],
  collectionKey: 'workforce-deployments',
}), {
  basePath: '/v1/workforce-deployments',
  collectionKey: 'workforce-deployments',
  normalize: normalizeWorkforceDeployment,
  buildPayload: buildWorkforceDeploymentPayload,
});

export const farmerProducersApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/farmer-producers',
  normalize: normalizeFarmerProducer,
  buildPayload: buildFarmerProducerPayload,
  defaultPerPage: 200,
  itemKeys: ['production_system', 'productionSystem', 'herd_size_class', 'herdSizeClass', 'herd_registration_number', 'herdRegistrationNumber'],
  collectionKey: 'farmer-producers',
}), {
  basePath: '/v1/farmer-producers',
  collectionKey: 'farmer-producers',
  normalize: normalizeFarmerProducer,
  buildPayload: buildFarmerProducerPayload,
});

export const cooperativesApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/cooperatives',
  normalize: normalizeCooperative,
  buildPayload: buildCooperativePayload,
  defaultPerPage: 200,
  itemKeys: ['cooperative_name', 'organisation_name', 'registration_number', 'registrationNumber'],
  collectionKey: 'cooperatives',
}), {
  basePath: '/v1/cooperatives',
  collectionKey: 'cooperatives',
  normalize: normalizeCooperative,
  buildPayload: buildCooperativePayload,
});

export {
  buildQueryParams,
  extractCollection,
  extractItem,
  normalizeLivestockActor,
  normalizeActorQualification,
  normalizeWorkforceDeployment,
  normalizeFarmerProducer,
  normalizeCooperative,
  buildLivestockActorPayload,
  buildActorQualificationPayload,
  buildWorkforceDeploymentPayload,
  buildFarmerProducerPayload,
  buildCooperativePayload,
  ACTOR_TYPE_MAP,
  SEX_MAP,
  AGE_GROUP_MAP,
  STATUS_MAP,
  VERIFY_MAP,
  QUAL_TYPE_MAP,
  PRODUCTION_SYSTEM_MAP,
  DEPLOYMENT_TYPE_MAP,
  VEHICLE_STATUS_MAP,
  COOP_TYPE_MAP,
  REG_AUTH_MAP,
  BANK_STATUS_MAP,
};
